import { noChange } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import type { ElementPart } from 'lit'
import { Subscription, timer } from 'rxjs'
import { take, tap } from 'rxjs/operators'

export interface IntersectOptions {
	/** Fire callback only once then disconnect (default: false) */
	once?: boolean
	/** IntersectionObserver threshold (default: 0) */
	threshold?: number | number[]
	/** IntersectionObserver rootMargin (default: '0px') */
	rootMargin?: string
	/** Delay in ms before triggering callback (default: 0) */
	delay?: number
}

export interface IntersectCallbacks {
	/** Called when element enters viewport */
	onEnter?: () => void
	/** Called when element exits viewport */
	onExit?: () => void
	/** IntersectionObserver options */
	options?: IntersectOptions
}

type IntersectCallback = (isVisible: boolean, entry: IntersectionObserverEntry) => void

interface IntersectState {
	element: HTMLElement
	observer: IntersectionObserver
	callback: IntersectCallback
	onEnter?: () => void
	onExit?: () => void
	once: boolean
	delay: number
	delayTimerSub?: Subscription
	hasFired: boolean
}

class IntersectDirective extends AsyncDirective {
	private state: IntersectState | null = null

	render(callbackOrOptions: IntersectCallback | IntersectCallbacks, options?: IntersectOptions) {
		void callbackOrOptions
		void options
		return noChange
	}

	override update(
		part: ElementPart,
		[callbackOrOptions, options]: [IntersectCallback | IntersectCallbacks, IntersectOptions?],
	) {
		const element = part.element as HTMLElement

		let callback: IntersectCallback | undefined
		let onEnter: (() => void) | undefined
		let onExit: (() => void) | undefined
		let finalOptions: IntersectOptions = {}

		if (typeof callbackOrOptions === 'function') {
			callback = callbackOrOptions
			finalOptions = options || {}
		} else {
			onEnter = callbackOrOptions.onEnter
			onExit = callbackOrOptions.onExit
			finalOptions = callbackOrOptions.options || {}
			callback = isVisible => {
				if (isVisible && onEnter) onEnter()
				if (!isVisible && onExit) onExit()
			}
		}

		// Skip if already set up for this element
		if (this.state?.element === element) {
			return noChange
		}

		// Cleanup previous state
		this.cleanup()

		const { once = false, threshold = 0, rootMargin = '0px', delay = 0 } = finalOptions

		// Create observer
		const observer = new IntersectionObserver(
			entries => {
				if (!this.state) return
				const entry = entries[0]
				const isVisible = entry.isIntersecting

				if (delay > 0 && isVisible) {
					if (this.state.delayTimerSub) {
						this.state.delayTimerSub.unsubscribe()
					}
					this.state.delayTimerSub = timer(delay)
						.pipe(
							take(1),
							tap(() => this.triggerCallback(isVisible, entry)),
						)
						.subscribe()
				} else {
					if (!isVisible && this.state.delayTimerSub) {
						this.state.delayTimerSub.unsubscribe()
						this.state.delayTimerSub = undefined
					}
					this.triggerCallback(isVisible, entry)
				}
			},
			{ threshold, rootMargin },
		)

		this.state = {
			element,
			observer,
			callback: callback!,
			onEnter,
			onExit,
			once,
			delay,
			hasFired: false,
		}

		observer.observe(element)

		return noChange
	}

	private triggerCallback(isVisible: boolean, entry: IntersectionObserverEntry): void {
		if (!this.state) return

		if (this.state.once) {
			if (isVisible && !this.state.hasFired) {
				this.state.hasFired = true
				this.state.callback(isVisible, entry)
				this.cleanup()
			}
			return
		}

		this.state.callback(isVisible, entry)
	}

	private cleanup(): void {
		if (!this.state) return

		this.state.delayTimerSub?.unsubscribe()
		this.state.delayTimerSub = undefined
		this.state.observer.disconnect()
		this.state = null
	}

	private pause(): void {
		if (!this.state) return
		this.state.delayTimerSub?.unsubscribe()
		this.state.observer.disconnect()
	}

	override disconnected(): void {
		this.pause()
	}

	override reconnected(): void {
		if (this.state) {
			this.state.observer.observe(this.state.element)
		}
	}
}

export const intersect = directive(IntersectDirective)
